import { Link, useParams, Navigate } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { BLOG_POSTS } from "../data/blog";
import { ArrowUpRight, ArrowLeft, Calendar, Clock } from "lucide-react";
import { CONTACT } from "../data/site";
import { SEO } from "../components/SEO";
import { articleSchema } from "../data/seo";

export function BlogIndex() {
  return (
    <div data-testid="blog-page" className="pt-32">
      <SEO
        title="Journal | Christian Counseling Insights | Beyond Blessed"
        description="Reflections on Christian counseling, trauma recovery, boundaries and faith-filled healing from Beyond Blessed in Wesley Chapel, FL."
        path="/blog"
      />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-14">
        <Reveal>
          <p className="uppercase tracking-[0.3em] text-xs text-teal mb-4">Journal</p>
          <h1 className="font-serif text-5xl md:text-7xl text-navy leading-none">Notes on healing,<br /><span className="italic text-teal">faith &amp; freedom.</span></h1>
          <p className="text-charcoal/70 max-w-xl mt-6">Reflections on Christian counseling, trauma recovery, boundaries, and the sacred work of restoration.</p>
        </Reveal>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24 grid md:grid-cols-2 gap-8" data-testid="blog-list">
        {BLOG_POSTS.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.1}>
            <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block bg-white border border-gold/25 hover:border-teal transition-all duration-500 h-full">
              <div className="overflow-hidden aspect-[16/10]">
                <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-8">
                <div className="flex items-center gap-4 text-xs uppercase tracking-widest text-teal mb-4">
                  <span>{p.tag}</span>
                  <span className="text-gold">•</span>
                  <span className="text-charcoal/50 flex items-center gap-1"><Clock size={12} /> {p.readTime}</span>
                </div>
                <h2 className="font-serif text-2xl md:text-3xl text-navy leading-tight mb-3 group-hover:text-teal transition-colors">{p.title}</h2>
                <p className="text-charcoal/70 mb-6">{p.excerpt}</p>
                <span className="inline-flex items-center gap-1 text-navy text-sm font-medium">
                  Read the article <ArrowUpRight size={15} className="group-hover:rotate-45 transition-transform duration-300" />
                </span>
              </div>
            </Link>
          </Reveal>
        ))}
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  return (
    <article data-testid="blog-post" className="pt-32 pb-24">
      <SEO
        title={`${post.title} | Beyond Blessed`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.cover}
        jsonLd={articleSchema(post)}
      />
      <div className="max-w-3xl mx-auto px-6 lg:px-10">
        <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-navy/60 hover:text-navy text-sm mb-8">
          <ArrowLeft size={15} /> Back to journal
        </Link>
        <Reveal>
          <div className="flex items-center gap-4 text-xs uppercase tracking-widest text-teal mb-5">
            <span>{post.tag}</span><span className="text-gold">•</span>
            <span className="text-charcoal/50 flex items-center gap-1"><Calendar size={12} /> {post.date}</span>
            <span className="text-gold">•</span>
            <span className="text-charcoal/50 flex items-center gap-1"><Clock size={12} /> {post.readTime}</span>
          </div>
          <h1 className="font-serif text-4xl md:text-6xl text-navy leading-tight mb-8">{post.title}</h1>
        </Reveal>
      </div>
      <div className="max-w-5xl mx-auto px-6 lg:px-10 mb-14">
        <div className="aspect-[21/9] overflow-hidden">
          <img src={post.cover} alt={post.title} className="w-full h-full object-cover" />
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-6 lg:px-10 space-y-6" data-testid="blog-body">
        {post.body.map((b, i) =>
          b.h ? <h2 key={i} className="font-serif text-2xl md:text-3xl text-navy mt-10 mb-3">{b.h}</h2>
              : <p key={i} className="text-charcoal/80 leading-relaxed text-lg">{b.p}</p>
        )}
      </div>
      <div className="max-w-2xl mx-auto px-6 lg:px-10 mt-14 pt-10 border-t border-gold/25 text-center">
        <p className="font-script text-3xl text-gold mb-3">Ready to begin?</p>
        <p className="text-charcoal/70 mb-6">Schedule a consultation and take the next step toward faith-filled healing.</p>
        <a href={CONTACT.request} target="_blank" rel="noreferrer" data-testid="blog-cta" className="bg-navy text-cream px-8 py-4 rounded-full inline-flex items-center gap-2 hover:bg-teal transition-colors">
          Request Appointment <ArrowUpRight size={17} />
        </a>
      </div>
    </article>
  );
}
